import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

const initialState = {
  wishlistItems: JSON.parse(localStorage.getItem("wishlistItems")) || [],
  loading: false,
  error: null,
};

const wishlistSlice = createSlice({
  name: "wishlist",
  initialState,
  reducers: {
    setLoading: (state) => {
      state.loading = true;
    },
    setError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    addToWishlist: (state, action) => {
      if (Array.isArray(action.payload)) {
        state.wishlistItems = action.payload;
      } else {
        const product = action.payload;
        const existingProduct = state.wishlistItems.find(
          (item) => item._id === product._id
        );
        if (!existingProduct) {
          state.wishlistItems.push(product);
          if (!localStorage.getItem("token")) {
            localStorage.setItem(
              "wishlistItems",
              JSON.stringify(state.wishlistItems)
            );
          }
        }
      }
      state.loading = false;
      state.error = null;
    },
    removeFromWishlist: (state, action) => {
      const productId = action.payload;
      state.wishlistItems = state.wishlistItems.filter(
        (item) => item._id !== productId
      );
      if (!localStorage.getItem("token")) {
        localStorage.setItem(
          "wishlistItems",
          JSON.stringify(state.wishlistItems)
        );
      }
      state.loading = false;
      state.error = null;
    },
    setWishlistItems: (state, action) => {
      state.wishlistItems = action.payload || [];
      state.loading = false;
      state.error = null;
    },
  },
});

export const {
  setLoading,
  setError,
  addToWishlist,
  removeFromWishlist,
  setWishlistItems,
} = wishlistSlice.actions;

export const toggleWishlistApi = (productId) => async (dispatch, getState) => {
  const token = localStorage.getItem("token");

  // Handle non-logged in users
  if (!token) {
    const currentWishlist = getState().wishlist.wishlistItems;
    const itemExists = currentWishlist.some((item) => item._id === productId);

    if (itemExists) {
      dispatch(removeFromWishlist(productId));
      toast.success("Item removed from wishlist");
    } else {
      // Make an API call to get product details
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/products/${productId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();

        if (data.success && data.responseObject) {
          dispatch(addToWishlist(data.responseObject));
          toast.success("Item added to wishlist");
        } else {
          throw new Error(data.message || "Failed to get product details");
        }
      } catch (error) {
        console.error("Error adding to wishlist:", error);
        toast.error("Failed to add item to wishlist");
      }
    }
    return;
  }

  dispatch(setLoading());
  try {
    // Check if the item exists in the current wishlist
    const currentWishlist = getState().wishlist.wishlistItems;
    const itemExists = currentWishlist.some((item) => item._id === productId);

    // Make either DELETE or POST request based on current status
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/users/wishlist/${productId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    const data = await response.json();
    const { success, message, responseObject } = data;
    // console.log("data in toggleWishlistApi", responseObject);
    if (success) {
      if (itemExists) {
        // If item was in wishlist, remove it
        dispatch(removeFromWishlist(productId));
        toast.success("Item removed from wishlist");
      } else {
        // If item wasn't in wishlist, add it
        dispatch(addToWishlist(responseObject));
        toast.success("Item added to wishlist");
      }
    } else {
      dispatch(setError(message));
      toast.error(message);
    }
  } catch (error) {
    dispatch(setError(error.message));
    toast.error("Failed to update wishlist");
  }
};

export const fetchWishlist = () => async (dispatch) => {
  const userLogin = localStorage.getItem("user");
  if (!userLogin) {
    return;
  }

  dispatch(setLoading());
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/users/wishlist`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
    const data = await response.json();
    // console.log("data in fetchWishlist", data);
    const { success, responseObject } = data;
    if (success) {
      dispatch(setWishlistItems(responseObject));
    }
  } catch (error) {
    dispatch(setError(error.message));
    toast.error("Failed to fetch wishlist");
  }
};

export const mergeWishlistsAfterLogin = () => async (dispatch, getState) => {
  const localWishlist = JSON.parse(localStorage.getItem("wishlistItems")) || [];
  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user"));

  if (!localWishlist.length || !token || !user) {
    dispatch(fetchWishlist());
    return;
  }

  try {
    // Call the merge wishlist API endpoint
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/users/merge-wishlist`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId: user.id,
          items: localWishlist.map((item) => item._id),
        }),
      }
    );

    const data = await response.json();
    if (data.success) {
      // Update the wishlist state with merged data
      dispatch(setWishlistItems(data.responseObject));
      // Clear local storage wishlist after successful merge
      localStorage.removeItem("wishlistItems");
    } else {
      throw new Error(data.message || "Failed to merge wishlists");
    }
  } catch (error) {
    console.error("Error merging wishlists:", error);
    toast.error("Failed to merge wishlists");
  }
};

export default wishlistSlice.reducer;
